<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import {Icon} from '@iconify/vue';
import {useFileHistoryStore} from '@/stores/useFileHistoryStore';
import type {HistoryEntry, HistoryKind} from '@/services/historyDb';
import FileHistoryPreview from "@/components/file-history/FileHistoryPreview.vue";
import {ChordInferenceBadge} from "@/components/chord-inference";
import {focusWithoutScroll} from '@/shared/focus';

const {entry} = defineProps<{
  entry: HistoryEntry;
}>();

const historyStore = useFileHistoryStore();

const isBusy = ref(false);
const dialogRef = ref<HTMLElement | null>(null);

// Anzeigename (P2.2): Eintrag kann benannt werden; leer → zurück auf den Dateinamen.
const displayName = computed(() => entry.name || entry.filename);
const nameDraft = ref(entry.name ?? '');
function saveName() {
  void historyStore.rename(entry.id, nameDraft.value);
}

// A11y: ESC schließt zuverlässig (globaler Listener statt fokusabhängigem @keydown),
// Fokus beim Öffnen in den Dialog setzen.
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close();
}
onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  focusWithoutScroll(dialogRef.value);
});
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

const kindLabel: Record<HistoryKind, string> = {
  upload: 'Original',
  transposed: 'Transponiert',
  shifted: 'Verschoben',
  merged: 'Zusammengeführt',
  'mode-changed': 'Variantwechsel',
  rebarred: 'Taktart geändert',
  edited: 'Bearbeitet',
  converted: 'Griffschrift',
};

const dateLabel = computed(() => new Date(entry.createdAt).toLocaleString('de-DE'));

const sizeLabel = computed(() => {
  const bytes = entry.size;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
});

const semitoneLabel = computed(() => {
  const s = entry.transposedSemitones ?? 0;
  if (s === 0) return '0 (keine)';
  return s > 0 ? `+${s} Halbtöne` : `${s} Halbtöne`;
});

const details = computed<Array<{label: string; value: string}>>(() => {
  const a = entry.analysisSummary;
  const rows: Array<{label: string; value: string}> = [
    {label: 'Typ', value: kindLabel[entry.kind]},
    {label: 'Dateiname', value: entry.filename},
    {label: 'Erstellt', value: dateLabel.value},
    {label: 'Größe', value: sizeLabel.value},
    {label: 'Transposition', value: semitoneLabel.value},
  ];
  if (a?.key) rows.push({label: 'Tonart', value: a.key});
  if (a?.timeSignature) rows.push({label: 'Taktart', value: a.timeSignature});
  if (a?.numMeasures != null) rows.push({label: 'Takte', value: String(a.numMeasures)});
  if (a?.numNotes != null) rows.push({label: 'Noten', value: String(a.numNotes)});
  if (a?.unmappableCount != null) rows.push({label: 'Unspielbar', value: String(a.unmappableCount)});
  return rows;
});

const settingsRows = computed<Array<{key: string; value: string}>>(() => {
  const settings = entry.settings;
  if (!settings) return [];
  return Object.entries(settings)
    .filter(([, v]) => v !== undefined && v !== null && typeof v !== 'object')
    .map(([key, value]) => ({key, value: String(value)}));
});

function close() {
  historyStore.closeDetail();
}

async function withBusy(fn: () => Promise<void>) {
  if (isBusy.value) return;
  isBusy.value = true;
  try {
    await fn();
  } finally {
    isBusy.value = false;
  }
}

function onReuse() {
  void withBusy(async () => {
    await historyStore.reuse(entry);
    close();
  });
}

function onDownload() {
  void withBusy(() => historyStore.download(entry));
}

function onOpenInNewTab() {
  historyStore.openInNewTab(entry);
}
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
  >
    <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        @click.self="close"
    >
      <div
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          class="flex max-h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl outline-none"
      >
        <!-- Kopf -->
        <header class="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
          <h2 class="flex min-w-0 items-center gap-2 text-base font-semibold text-gray-900">
            <Icon icon="fa6-solid:circle-info" class="shrink-0 text-blue-500" />
            <span class="truncate">{{ displayName }}</span>
          </h2>
          <button type="button" class="btn btn-ghost !px-2 !py-1.5" aria-label="Schließen" @click="close">
            <Icon icon="fa6-solid:xmark" />
          </button>
        </header>

        <!-- Inhalt -->
        <div class="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <!-- Name (optional) -->
          <section>
            <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Name</label>
            <input
                v-model="nameDraft"
                :placeholder="entry.filename"
                class="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                @blur="saveName"
                @keydown.enter="saveName"
            />
          </section>

          <!-- Details -->
          <section>
            <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Details</h3>
            <dl class="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
              <div v-for="row in details" :key="row.label" class="flex justify-between gap-3 border-b border-gray-50 py-0.5">
                <dt class="text-gray-500">{{ row.label }}</dt>
                <dd class="truncate text-right font-medium text-gray-800" :title="row.value">{{ row.value }}</dd>
              </div>
            </dl>
          </section>

          <!-- Automatische Akkorderkennung: alle vier Zustände, inkl. „noch nicht analysiert" -->
          <section v-if="entry.chordInference">
            <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Akkorderkennung</h3>
            <ChordInferenceBadge :status="entry.chordInference"/>
          </section>

          <!-- Verwendete Settings -->
          <section v-if="settingsRows.length">
            <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Verwendete Settings</h3>
            <div class="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
              <div v-for="row in settingsRows" :key="row.key" class="flex justify-between gap-3">
                <span class="truncate font-mono text-gray-500" :title="row.key">{{ row.key }}</span>
                <span class="font-medium text-gray-800">{{ row.value }}</span>
              </div>
            </div>
          </section>

          <!-- Vorschau -->
        <FileHistoryPreview :entryId="entry.id" />
        </div>

        <!-- Aktionen -->
        <footer class="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" class="btn btn-secondary" :disabled="isBusy" @click="onOpenInNewTab">
            <Icon icon="fa6-solid:arrow-up-right-from-square" class="mr-1.5 text-sm" /> In neuem Tab
          </button>
          <button type="button" class="btn btn-secondary" :disabled="isBusy" @click="onDownload">
            <Icon icon="fa6-solid:download" class="mr-1.5 text-sm" /> Download
          </button>
          <button type="button" class="btn btn-primary" :disabled="isBusy" @click="onReuse">
            <Icon icon="fa6-solid:arrow-rotate-left" class="mr-1.5 text-sm" /> In Editor laden
          </button>
        </footer>
      </div>
    </div>
  </Transition>
</template>
